import { formatInTimeZone } from "date-fns-tz";

/**
 * The timezone a WasteSense report's week is bucketed in.
 *
 * This MUST match `WASTESENSE_SCHEDULE.timezone` in
 * `features/a3s-reports/src/plugins/wastesense/window.ts`: the report's
 * `periodStart` (its DynamoDB sort key) is derived from the cron tick's LOCAL
 * calendar day in that schedule timezone — deliberately NOT the site's own
 * timezone. So to compute a report's `periodStart` from an arbitrary instant
 * client-side we must resolve that instant into the same schedule zone, or the
 * Monday we compute could land on the wrong week and address a different (or
 * non-existent) report row.
 *
 * Hard-coded here rather than imported from the reports package because the
 * reports `window.ts` is server-side (it lives beside AWS-coupled code); this
 * is the frontend's mirror of the same constant.
 */
const WASTESENSE_TIMEZONE = "Australia/Sydney";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Compute the `periodStart` (Monday, `YYYY-MM-DD`) of the WasteSense report
 * week that contains `timestampIso`.
 *
 * This mirrors the Monday arithmetic in the reports feature's `windowFor`
 * (`features/a3s-reports/src/plugins/wastesense/window.ts`) so the value we
 * produce is byte-identical to the report row's `periodStart` sort key. The one
 * deliberate difference: `windowFor` reports on *yesterday's* week (it fires
 * from a 06:00 cron and subtracts a day first), whereas here we want the week
 * containing the dump's OWN local day — so there is no "minus one day" step.
 *
 * Steps, matching `windowFor`:
 *  1. Resolve the instant to its calendar day in the schedule timezone (NOT the
 *     UTC day — a dump just before local midnight can fall on a different UTC
 *     day, which would bucket it into the wrong week).
 *  2. Re-anchor that calendar day at UTC midnight purely for day arithmetic.
 *     These are date-only labels, so fixed-offset UTC stepping sidesteps DST.
 *  3. Step back `(isoWeekday - 1)` days to the Monday of that week (ISO weekday:
 *     Mon=1 … Sun=7).
 */
export function wasteSenseWeekStart(timestampIso: string): string {
  // 1. The dump's calendar day in the schedule timezone.
  const localDate = formatInTimeZone(new Date(timestampIso), WASTESENSE_TIMEZONE, "yyyy-MM-dd");

  // 2. Re-anchor that calendar day at UTC midnight for pure day arithmetic.
  const anchor = new Date(`${localDate}T00:00:00Z`);

  // 3. Monday of the week containing that day. getUTCDay is Sun=0; map Sun → 7
  //    so the ISO weekday is Mon=1 … Sun=7, then step back to the Monday.
  const isoWeekday = anchor.getUTCDay() === 0 ? 7 : anchor.getUTCDay();
  const mondayUtc = new Date(anchor.getTime() - (isoWeekday - 1) * DAY_MS);

  return mondayUtc.toISOString().slice(0, 10);
}
